import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// The header dropdowns have to work two ways. On a desktop pointer they open on
// hover, which is what the site has always done. On a touch screen there is no
// hover: the first tap used to just follow the parent link, so the submenu was
// unreachable. There we show a toggle next to the label instead - tap the label
// to go to the page, tap the toggle to expand the litters or dogs in place.
const POINTER_QUERY = '(hover: hover) and (min-width: 881px)';

function usePointerNav() {
  const [isPointer, setIsPointer] = useState(
    () => window.matchMedia?.(POINTER_QUERY).matches ?? true
  );

  useEffect(() => {
    const mq = window.matchMedia?.(POINTER_QUERY);
    if (!mq) return undefined;

    const onChange = event => setIsPointer(event.matches);
    // Safari before 14 only has the deprecated listener API.
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
      return () => mq.removeEventListener('change', onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return isPointer;
}

export default function NavDropdown({
  to,
  label,
  items,
  caption,
  footer,
  onNavClick,
  onToggleSubmenu,
  parentMenuOpen
}) {
  const isPointer = usePointerNav();
  const [open, setOpen] = useState(false);

  // Swapping between hover and tap (rotating a tablet, resizing a window) must
  // not leave a menu stuck open with no way to close it.
  useEffect(() => setOpen(false), [isPointer]);

  // Closing the hamburger menu collapses the submenus inside it, so reopening
  // it starts from the top rather than wherever it was left.
  useEffect(() => {
    if (parentMenuOpen === false) setOpen(false);
  }, [parentMenuOpen]);

  const report = (name, item) => {
    setOpen(false);
    if (onNavClick) onNavClick(name, item);
  };

  if (!items || items.length === 0) {
    return (
      <Link to={to} onClick={() => report(label)}>{label}</Link>
    );
  }

  // Hover and keyboard focus drive the menu on a pointer; on touch the toggle
  // button does, so the parent link stays a plain link to the page.
  const pointerHandlers = isPointer
    ? {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => setOpen(false),
        onFocus: () => setOpen(true),
        onBlur: event => {
          if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
        }
      }
    : {};

  return (
    <div className="nav-item" {...pointerHandlers}>
      <div className="nav-item-row">
        <Link to={to} onClick={() => report(label)}>{label}</Link>
        {!isPointer && (
          <button
            type="button"
            className={`nav-item-toggle ${open ? 'open' : ''}`}
            aria-expanded={open}
            aria-label={`${open ? 'Hide' : 'Show'} ${label.toLowerCase()} menu`}
            onClick={() => {
              setOpen(!open);
              // Only the touch toggle reports: hovering on a desktop is not a
              // deliberate act the way tapping this is.
              if (onToggleSubmenu) onToggleSubmenu(!open);
            }}
          >
            <span className="chevron"></span>
          </button>
        )}
      </div>
      {open && (
        <div className="dropdown-menu">
          {caption && <span className="dropdown-caption">{caption}</span>}
          {items.map(item => (
            <Link to={item.to} key={item.key} onClick={() => report(item.label, item)}>
              {item.label}
            </Link>
          ))}
          {footer && (
            <>
              <span className="dropdown-divider"></span>
              <Link to={footer.to} onClick={() => report(footer.label, footer)}>{footer.label}</Link>
            </>
          )}
        </div>
      )}
    </div>
  );
}
